import { Grid, Typography } from '@mui/material';
import { AmlStatusMap, OrderSide, TransactionTypeChar } from '@tassat/common-definitions';
import { AccountWithdrawalDataRecord } from '@tassat/fred-definitions';
import { TransactionHistoryRecord } from '@tassat/fred-extended-definitions';
import { ButtonType, TransactionDetail, WalletAddress } from '@tassat/text';
import { formatEpochDate } from '@tassat/utility';
import React from 'react';

import { muiStyles } from 'SharedComponents/transactionDetails/transactionFirmDetails/styles';
import WithdrawalInstructionDetails from 'SharedComponents/withdrawalInstructions/withdrawalInstructionDetails';

import { TransactionDetailsProps } from '../types';

interface TransactionFirmDetail {
    currentUser: TransactionDetailsProps['currentUser'];
    selectedTransaction: TransactionDetailsProps['selectedTransaction'];
    withdrawalData?: AccountWithdrawalDataRecord;
}

const fromFirmMappedData = (transaction: TransactionHistoryRecord): TransactionHistoryRecord => {
    if (
        transaction.type !== TransactionTypeChar.deposit &&
        transaction.type !== TransactionTypeChar.int_distribution
    ) {
        if (transaction.type === TransactionTypeChar.withdrawal) {
            return ({
                ...transaction,
                SenderFirm: transaction.RecipientFirm,
                SenderAccount: transaction.RecipientAccount,
                SenderAccountName: transaction.RecipientAccountName,
                SenderAddress: transaction.RecipientAddress
            });
        }

        return transaction;
    } else {
        return ({
            ...transaction,
            SenderFirm: '-',
            SenderAccount: '-',
            SenderAccountName: '-',
            SenderUsername: '-'
        });
    }
};

const toFirmMappedData = (transaction: TransactionHistoryRecord): TransactionHistoryRecord => {
    if (transaction.type === TransactionTypeChar.deposit) {
        return ({
            ...transaction,
            RecipientFirm: transaction.SenderFirm,
            RecipientAccount: transaction.SenderAccount,
            RecipientAccountName: transaction.SenderAccountName,
            RecipientAddress: transaction.SenderAddress
        });
    } else {
        return ({
            ...transaction,
            RecipientFirm: transaction.RecipientFirm ?? '-',
            RecipientAccount: transaction.RecipientAccount ?? '-',
            RecipientAccountName: transaction.RecipientAccountName,
            RecipientAddress: transaction.RecipientAddress
        });
    }
};

export const FromFirmDetail = (props: TransactionFirmDetail) => {
    const {
        currentUser,
        selectedTransaction
    } = props;

    const classes = muiStyles(props);
    const mappedTransactionData = fromFirmMappedData(selectedTransaction);

    return (
        <Grid
            className={`${classes.detailSection} ${classes.detailSectionHighlighted}`}
            container
            direction='column'
            item
            spacing={0}
            wrap='nowrap'
        >
            <TransactionDetail
                id='transactionDetails_senderFirm'
                label='From Firm'
            />
            <Grid
                className={classes.firmDetails}
                container
                direction='row'
                justifyContent='space-between'
                spacing={0}
                wrap='nowrap'
            >
                <Typography
                    className={classes.firmAttribute}
                    id='transactionDetails_senderFirmName_value'
                    variant='body1'
                >
                    {
                        (mappedTransactionData.type !== TransactionTypeChar.deposit) ?
                            mappedTransactionData.SenderFirm : '-'
                    }
                </Typography>
                <Typography
                    className={classes.firmAttribute}
                    id='transactionDetails_senderAccount_value'
                    variant='body1'
                >
                    {
                        (mappedTransactionData.type !== TransactionTypeChar.deposit) ?
                            mappedTransactionData.SenderAccount : '-'
                    }
                </Typography>
            </Grid>
            {(
                mappedTransactionData.type !== TransactionTypeChar.deposit &&
                mappedTransactionData.type !== TransactionTypeChar.int_distribution
            ) &&
            <TransactionDetail
                id='transactionDetails_senderWallet'
                label='Account'
            >
                <WalletAddress
                    buttonType={ButtonType.SMALL}
                    className={classes.attribute}
                    id='transactionDetails_senderWallet_value'
                    isAdmin={currentUser.IsAdmin}
                    walletAddress={mappedTransactionData.SenderAddress ?? '-'}
                />
            </TransactionDetail>}
            <TransactionDetail
                attribute={
                    (mappedTransactionData.type !== TransactionTypeChar.deposit) ?
                        mappedTransactionData.SenderAccountName
                        : '-'
                }
                id='transactionDetails_senderAccountName'
                label='Account Name'
            />
            {currentUser.IsAdmin && (
                selectedTransaction.type === TransactionTypeChar.send ||
                selectedTransaction.type === TransactionTypeChar.transfer
            ) && mappedTransactionData.AMLEnabled &&
            <>
                <TransactionDetail
                    attribute={AmlStatusMap[selectedTransaction.AMLSenderStatus]?.displayValue || '-'}
                    id='transactionDetails_amlStatus'
                    label='AML Status'
                />
                <TransactionDetail
                    attribute={mappedTransactionData.AMLSenderReason ? mappedTransactionData.AMLSenderReason : '-'}
                    id='transactionDetails_amlReason'
                    label='AML Reason'
                />
                <TransactionDetail
                    attribute={mappedTransactionData.AMLSenderValidationId ? mappedTransactionData.AMLSenderValidationId : '-'}
                    id='transactionDetails_amlValidationId'
                    label='AML Validation ID'
                />
                <TransactionDetail
                    attribute={mappedTransactionData.AMLSenderJourneyId ? mappedTransactionData.AMLSenderJourneyId : '-'}
                    id='transactionDetails_amlJourneyId'
                    label='AML Journey ID'
                />
            </>}
            {(
                mappedTransactionData.type === TransactionTypeChar.send ||
                mappedTransactionData.type === TransactionTypeChar.order ||
                mappedTransactionData.type === TransactionTypeChar.deposit ||
                mappedTransactionData.type === TransactionTypeChar.withdrawal ||
                mappedTransactionData.type === TransactionTypeChar.int_distribution
            ) &&
            <TransactionDetail
                attribute={
                    (mappedTransactionData.type !== TransactionTypeChar.deposit) ?
                        mappedTransactionData.SenderUsername
                        : '-'
                }
                id='transactionDetails_senderUsername'
                label='Username'
            />}
            {(
                mappedTransactionData.type === TransactionTypeChar.send ||
                mappedTransactionData.type === TransactionTypeChar.order ||
                mappedTransactionData.type === TransactionTypeChar.deposit ||
                mappedTransactionData.type === TransactionTypeChar.withdrawal ||
                mappedTransactionData.type === TransactionTypeChar.int_distribution
            ) &&
            <>
                <TransactionDetail
                    attribute={
                        mappedTransactionData.ApproverUsername ||
                        mappedTransactionData.Approver ||
                        mappedTransactionData.ApproverEmail ||
                        '-'
                    }
                    id='transactionDetails_approverUsername'
                    label='Approver Username'
                />
                <TransactionDetail
                    attribute={
                        mappedTransactionData.Approver &&
                        formatEpochDate(mappedTransactionData.ApproveTime) ||
                        '-'
                    }
                    id='transactionDetails_approveTime'
                    label='Approve Time'
                />
            </>}
            {mappedTransactionData.type === TransactionTypeChar.withdrawal && (
                <TransactionDetail
                    attribute={selectedTransaction.RequestId}
                    id='transactionDetails_withdrawalRequestId'
                    label='Request ID'
                />
            )}
        </Grid>
    );
};

export const ToFirmDetail = (props: TransactionFirmDetail) => {
    const {
        selectedTransaction,
        currentUser,
        withdrawalData
    } = props;

    const classes = muiStyles(props);
    const mappedTransactionData = toFirmMappedData(selectedTransaction);

    let recipientFirmTile = 'To Firm';
    const isBrokerDetail = (
        mappedTransactionData.type === TransactionTypeChar.order &&
        (!mappedTransactionData.OrderSide.localeCompare(OrderSide.BUY, undefined, { sensitivity: 'base' }) ||
            !mappedTransactionData.OrderSide.localeCompare(OrderSide.SELL, undefined, { sensitivity: 'base' }))
    );

    if (mappedTransactionData.type === TransactionTypeChar.order && !isBrokerDetail) {
        recipientFirmTile = 'To Fund';
    }

    const withdrawalInstruction = selectedTransaction.WithdrawalDetails ?? withdrawalData;

    return (
        <Grid
            className={`${classes.detailSection} ${classes.detailSectionHighlighted}`}
            container
            direction='column'
            item
            spacing={0}
            wrap='nowrap'
        >
            <TransactionDetail
                id='transactionDetails_recipientFirm'
                label={recipientFirmTile}
            />
            <Grid
                className={classes.firmDetails}
                container
                direction='row'
                justifyContent='space-between'
                spacing={0}
                wrap='nowrap'
            >
                {
                    mappedTransactionData.type !== TransactionTypeChar.withdrawal ?
                        <>
                            <Typography
                                className={classes.firmAttribute}
                                id='transactionDetails_recipientFirmName_value'
                                variant='body1'
                            >
                                {mappedTransactionData.RecipientFirm}
                            </Typography>
                            {mappedTransactionData.type !== TransactionTypeChar.order &&
                                <Typography
                                    className={classes.firmAttribute}
                                    id='transactionDetails_recipientAccount_value'
                                    variant='body1'
                                >
                                    {mappedTransactionData.RecipientAccount}
                                </Typography>}
                        </> : <></>
                }

            </Grid>
            {
                mappedTransactionData.type === TransactionTypeChar.withdrawal && withdrawalInstruction ?
                    <>
                        <WithdrawalInstructionDetails
                            displayComponent='TransactionDetail'
                            withdrawalInstruction={withdrawalInstruction}
                        />
                    </>
                    : (
                        <>
                            {(
                                mappedTransactionData.type !== TransactionTypeChar.order
                            ) &&
                            (
                                <>
                                    <TransactionDetail
                                        id='transactionDetails_recipientAccount'
                                        label='Account'
                                    >
                                        <WalletAddress
                                            buttonType={ButtonType.SMALL}
                                            className={classes.attribute}
                                            id='transactionDetails_recipientAccount_value'
                                            isAdmin={currentUser.IsAdmin}
                                            walletAddress={mappedTransactionData.RecipientAddress}
                                        />
                                    </TransactionDetail>
                                    <TransactionDetail
                                        attribute={mappedTransactionData.RecipientAccountName}
                                        id='transactionDetails_recipientAccountName'
                                        label='Account Name'
                                    />
                                    <TransactionDetail
                                        attribute={mappedTransactionData.RequestId ?? '-'}
                                        id='transactionDetails_transactionRequestId'
                                        label='Request ID'
                                    />
                                    {currentUser.IsAdmin && (
                                        selectedTransaction.type === TransactionTypeChar.send ||
                                        selectedTransaction.type === TransactionTypeChar.transfer
                                    ) && mappedTransactionData.AMLEnabled &&
                                    <>
                                        <TransactionDetail
                                            attribute={AmlStatusMap[selectedTransaction.AMLRecipientStatus]?.displayValue || '-'}
                                            id='transactionDetails_amlStatus'
                                            label='AML Status'
                                        />
                                        <TransactionDetail
                                            attribute={mappedTransactionData.AMLRecipientReason ? mappedTransactionData.AMLRecipientReason : '-'}
                                            id='transactionDetails_amlReason'
                                            label='AML Reason'
                                        />
                                        <TransactionDetail
                                            attribute={mappedTransactionData.AMLRecipientValidationId ? mappedTransactionData.AMLRecipientValidationId : '-'}
                                            id='transactionDetails_amlValidationId'
                                            label='AML Validation ID'
                                        />
                                        <TransactionDetail
                                            attribute={mappedTransactionData.AMLRecipientJourneyId ? mappedTransactionData.AMLRecipientJourneyId : '-'}
                                            id='transactionDetails_amlJourneyId'
                                            label='AML Journey ID'
                                        />
                                    </>}
                                </>
                            )}
                        </>)
            }
        </Grid>
    );
};